"use client";

import { Button, ContentfulImage, ContentLayout, ItemsContainer } from "@2u-landing-pages/components";
import { Banner } from "@2u-landing-pages/templates";
import {
  ContentfulImageConfig,
  TypeButtonWithoutUnresolvableLinksResponse,
  TypeContent_fullWithoutUnresolvableLinksResponse,
  TypeItems_containerWithoutUnresolvableLinksResponse,
  TypeProgram_metaWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { camelCase, generateId, useIntersectionObserver } from "@2u-landing-pages/utils";
import { renderEmbeddedEntry, renderText } from "@2u-landing-pages/utils/contentful-richtext";
import EventClient from "@2u-landing-pages/utils/event-client";
import { useMetadataParser } from "@2u-landing-pages/utils/useMetadataParser";
import { useContentfulInspectorMode } from "@contentful/live-preview/react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { IconFeatherChevronDown, IconFeatherChevronUp } from "@red-digital/bricks";
import cn from "classnames";
import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { CustomCSSProperties, ProductCardProps } from "../../ProductCard.type";
// @ts-ignore
import styles from "./Default.module.scss";

export function trimProgramMetaName(str: string): string {
  const parts = str.trim().split("-");
  if (parts.length <= 2) return "";
  return parts.slice(1, -1).join("-").toLowerCase();
}

type AccordionItemProps = {
  content: any;
  parseMetadata: (template: string) => string;
  schoolProgramName: string;
};

const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ content, parseMetadata, schoolProgramName }, ref) => {
    const { body, open } = content?.fields || {};
    const [isExpanded, setIsExpanded] = useState(Boolean(open));
    const contentRef = useRef<HTMLDivElement>(null);

    const handleOnClick = () => {
      setIsExpanded((expanded) => {
        const isExpanded = !expanded;
        EventClient.elementClicked({
          webElement: {
            elementType: "Landing Page",
            htmlId: window.location.href,
            location: schoolProgramName,
            name: isExpanded ? "OPEN" : "CLOSED",
            text: isExpanded ? "Show less" : "Show more",
          },
        });
        return isExpanded;
      });
    };

    useEffect(() => {
      if (ref && typeof ref !== "function") {
        ref.current = contentRef.current;
      }
    }, [ref]);

    return (
      <div key={content?.sys?.id} className={styles.accordion}>
        <div ref={contentRef} className={`${styles.accordionContent} ${isExpanded ? styles.expanded : ""}`}>
          <div className={styles.accordionContentInner}>
            {body?.content?.length > 0 && (
              <>
                {documentToReactComponents(body, {
                  renderText: (text) => renderText(parseMetadata(text)),
                  renderNode: {
                    [BLOCKS.PARAGRAPH]: (node, children) => <div>{children}</div>,
                    [INLINES.EMBEDDED_ENTRY]: (node) => renderEmbeddedEntry(node, "Accordion Item", body),
                  },
                })}
              </>
            )}
          </div>
        </div>
        <button className={styles.accordionToggle} onClick={handleOnClick}>
          {isExpanded ? "Show less" : "Show more"}
          {isExpanded ? <IconFeatherChevronUp /> : <IconFeatherChevronDown />}
        </button>
      </div>
    );
  }
);

AccordionItem.displayName = "AccordionItem";

export const Default = forwardRef<HTMLDivElement, ProductCardProps>((props, ref) => {
  const {
    product,
    index = 0,
    currentPage = 1,
    lob,
    category,
    position,
    mode,
    standalone,
    className,
    extended = false,
  } = props;

  const inspector = useContentfulInspectorMode();
  const accordionRef = useRef<HTMLDivElement>(null);
  const viewCorrelationId = useRef<string>(generateId());
  const hasBeenViewed = useRef<boolean>(false);
  const hasBeenLoaded = useRef<boolean>(false);

  const {
    program_meta,
    product_card_id,
    eyebrow,
    eyebrow_color,
    heading,
    heading_color,
    product_card_flag,
    product_card_flag_text_color,
    product_card_flag_background_color,
    banner_top,
    banner_bottom,
    product_card_content,
    product_card_buttons,
    school_photo,
    school_photo_config,
    show_dropshadow,
    show_school_photo_desktop,
    show_school_photo_mobile,
    form,
    product_card_size,
    buttons_background_color,
  } = product?.fields || {};

  const { fields: programMeta, parseMetadata } = useMetadataParser(
    program_meta as TypeProgram_metaWithoutUnresolvableLinksResponse
  );

  const productEventData = {
    product: {
      productId: trimProgramMetaName(programMeta?.name) || "",
      name: programMeta?.topic || "",
      category: `mp-${lob}-${category}` || "",
      brand: programMeta?.school?.fields?.slug || "",
      variant: "",
      position,
    },
    viewCorrelationId: viewCorrelationId.current,
  };

  const photoConfig = school_photo_config as unknown as ContentfulImageConfig;

  const {
    taxi_package_id,
    main_content,
    footnote: formFootnote,
    button_background_color,
    button_hover_state,
    button_text_color,
    progress_bar_color,
    question_color,
    section_background_color,
  } = form?.fields || {};
  const { heading: formHeading, subhead: formSubhead, body: formBody, image } = main_content?.fields || {};

  const isRecommended = !extended && Boolean(product_card_flag?.content?.length) && index === 0 && currentPage === 1;
  const showSchoolPhoto = school_photo?.fields?.file?.url && !extended;

  const style: CustomCSSProperties = {};
  if (eyebrow_color) style["--eyebrow-color"] = `var(${eyebrow_color})`;
  if (heading_color) style["--heading-color"] = `var(${heading_color})`;
  if (show_school_photo_desktop) style["--show-school-photo-desktop"] = "block";
  if (show_school_photo_mobile) style["--show-school-photo-mobile"] = "block";
  if (product_card_flag_text_color) style["--product-card-flag-text-color"] = `var(${product_card_flag_text_color})`;
  if (product_card_flag_background_color)
    style["--product-card-flag-background-color"] = `var(${product_card_flag_background_color})`;
  if (buttons_background_color) style["--buttons-background-color"] = `var(${buttons_background_color})`;

  if (mode === "multi-program") {
    style["--buttons-background-color"] = undefined;
  }

  const handleIntersect = useCallback(() => {
    if (hasBeenViewed.current) return;
    hasBeenViewed.current = true;

    EventClient.productViewed(productEventData);
  }, []);

  const addObserver = useIntersectionObserver(handleIntersect, { threshold: 0.5 });
  const setObserver = useCallback(
    (element: HTMLElement | null) => {
      if (element) addObserver(element);
    },
    [addObserver]
  );

  useEffect(() => {
    if (hasBeenLoaded.current) return;
    hasBeenLoaded.current = true;

    EventClient.productLoaded(productEventData);
  }, []);

  const renderContent = (content: any) => {
    if (!content) return null;

    const contentType = content?.sys?.contentType?.sys?.id;
    const badge = content?.fields?.items?.some(
      (item: { fields?: { variation?: string } }) => item?.fields?.variation === "badge"
    );
    const grid = content?.fields?.desktop_layout_orientation === "grid" && !badge;
    const stacked = content?.fields?.desktop_layout_orientation === "stacked" && !badge;

    switch (contentType) {
      case "items_container":
        return (
          <ItemsContainer
            key={content?.sys?.id}
            className={cn(styles.itemsContainer, {
              [styles.itemsContainerGrid]: grid,
              [styles.itemsContainerStacked]: stacked,
              [styles.itemsContainerBadge]: badge,
            })}
            parseMetadata={parseMetadata}
            orientation="horizontal"
            entry={content as TypeItems_containerWithoutUnresolvableLinksResponse}
          />
        );
      case "accordion_item":
        return (
          <AccordionItem
            key={content?.sys?.id}
            content={content}
            parseMetadata={parseMetadata}
            ref={accordionRef}
            schoolProgramName={`${heading} - ${eyebrow}`}
          />
        );
      case "content_full":
        return (
          <ContentLayout key={content?.sys?.id} content={content as TypeContent_fullWithoutUnresolvableLinksResponse} />
        );
      default:
        return null;
    }
  };

  const handleInlineDrawerClick = (button: TypeButtonWithoutUnresolvableLinksResponse | undefined) => {
    if (!button) return;

    try {
      const eventData = {
        image_url: image?.fields?.file?.url || "",
        title: formHeading ? JSON.stringify(formHeading) : "",
        subtitle: formSubhead ? JSON.stringify(formSubhead) : "",
        body: formBody ? JSON.stringify(formBody) : undefined,
        form_data_package_id: taxi_package_id || "",
        formFootnote,
        colors: {
          button_background_color: button_background_color || "--surface-primary-6",
          button_hover_state,
          button_text_color,
          progress_bar_color,
          question_color,
          section_background_color,
        },
      };

      if (button.fields?.action === "OPEN_DRAWER") {
        const event = new CustomEvent("eduListing", { detail: eventData });
        window.dispatchEvent(event);
      }
    } catch (error) {
      console.error("Error creating drawer event data:", error);
    }
  };

  const combinedRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (typeof ref === "function") {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }

      if (element) {
        setObserver(element);
        (element as any).accordionRef = accordionRef;
      }
    },
    [ref, accordionRef]
  );

  if (!product?.fields) {
    return null;
  }

  return (
    <div
      ref={combinedRef}
      id={product_card_id || `product-card-${product?.sys?.id || ""}`}
      data-name={parseMetadata(`${programMeta?.school_name} - ${programMeta?.program_name}`)}
      className={cn(
        className,
        styles.productCard,
        product_card_size && standalone ? styles[camelCase(`card-size-${product_card_size}`)] : "",
        {
          [styles.recommended]: isRecommended,
          [styles.dropshadow]: show_dropshadow || extended,
          [styles.cardWithBanner]: banner_top,
          [styles.standalone]: standalone,
          [styles.extended]: extended,
        }
      )}
      style={style}
    >
      {isRecommended && (
        <div className={styles.recommendedFlag} style={style}>
          {product_card_flag && (
            <>
              {documentToReactComponents(product_card_flag, {
                renderText: (text) => renderText(parseMetadata(text)),
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => <div>{children}</div>,
                  [INLINES.EMBEDDED_ENTRY]: (node) => renderEmbeddedEntry(node, "Product Card Flag", product_card_flag),
                },
              })}
            </>
          )}
        </div>
      )}
      <div className={styles.card}>
        {banner_top && (
          <div className={styles.bannerTop}>
            <Banner
              className={cn("banner-top", styles.banner)}
              {...banner_top?.fields}
              inspector={inspector}
              location="Product Card Top Banner"
            />
          </div>
        )}

        <aside className={styles.featuredSection}>
          <div className={styles.featuredContent}>
            {programMeta?.logo && (
              <ContentfulImage image={programMeta?.logo} className={styles.logo} width={200} loading="lazy" />
            )}
            <div className={styles.heading}>
              {eyebrow && <p className={styles.subtitle}>{parseMetadata(eyebrow)}</p>}
              {heading && <h2 className={styles.title}>{parseMetadata(heading)}</h2>}
            </div>
          </div>
          {showSchoolPhoto && (
            <div
              className={cn(styles.featuredImage, {
                [styles.featuredImageDesktop]: show_school_photo_desktop,
              })}
            >
              <ContentfulImage
                key={`school-photo-${product?.sys?.id || ""}`}
                image={school_photo}
                config={photoConfig}
                loading="eager"
                {...inspector({
                  entryId: product?.sys?.id || "",
                  fieldId: "school_photo",
                })}
              />
            </div>
          )}
        </aside>

        <div className={styles.content}>
          <div className={styles.heading}>
            {eyebrow && <p className={styles.subtitle}>{parseMetadata(eyebrow)}</p>}
            {heading && <h2 className={styles.title}>{parseMetadata(heading)}</h2>}
          </div>
          {(product_card_content || [])?.map((content, idx) => (
            <React.Fragment key={content?.sys?.id || `content-${idx}`}>{renderContent(content)}</React.Fragment>
          ))}
        </div>

        {banner_bottom && (
          <div className={styles.bannerBottom}>
            <Banner
              className={cn("banner-bottom", styles.banner)}
              {...banner_bottom?.fields}
              inspector={inspector}
              location="Product Card Bottom Banner"
            />
          </div>
        )}

        <div className={styles.ctaSection}>
          {(product_card_buttons || [])?.length > 0 && (
            <div className={styles.buttonWrapper}>
              {product_card_buttons?.map((button) => {
                const { link_url, ...buttonPropsToPass } = button?.fields || {};

                return (
                  <Button
                    key={button?.sys?.id || `button-${Math.random()}`}
                    location={`${heading} - ${eyebrow}`}
                    {...buttonPropsToPass}
                    onClick={(e) => {
                      e.stopPropagation();
                      const correlationId = generateId();
                      const clickEventData = {
                        ...productEventData,
                        correlationId,
                      };

                      EventClient.productClicked(clickEventData);

                      if (button?.fields?.action === "OPEN_DRAWER") {
                        handleInlineDrawerClick(button);
                      } else if (button?.fields?.action === "OPEN_LINK") {
                        const destinationUrl = button?.fields?.link_url;
                        if (!destinationUrl) return;

                        EventClient.elementClicked({
                          webElement: {
                            elementType: "Product Card CTA Redirect",
                            htmlId: window.location.href,
                            location: "product card",
                            position: "CTA",
                            name: correlationId,
                            text: parseMetadata(button?.fields?.label || ""),
                          },
                        });

                        const urlParams = new URLSearchParams(window.location.search);
                        urlParams.append("utm_subId1", correlationId);
                        const redirectUrl = `${destinationUrl}?${urlParams.toString()}`;
                        window.open(redirectUrl, "_blank");
                      }
                    }}
                  >
                    {parseMetadata(button?.fields?.label || "")}
                  </Button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

Default.displayName = "ProductCard";
